<script setup lang="ts">
import { useRouter } from 'vue-router';
import BackButton from '@/js/account/components/navigation/BackButton.vue';
import LogoLink from '@/js/account/components/navigation/LogoLink.vue';
import { classNames } from '@/js/lib/bem';

withDefaults(
  defineProps<{
    src?: string
    alt?: string
  }>(),
  {
    alt: '',
  },
);

const cn = classNames('account-auth-layout');

const router = useRouter();

function goBack(): void {
  if (window.history.length > 1) {
    router.back();
    return;
  }

  window.location.assign('/');
}
</script>

<template>
  <div :class="cn()">
    <aside :class="cn('visual')" aria-hidden="true">
      <img v-if="src" :src="src" :alt="alt" :class="cn('image')">
      <div v-else :class="cn('fallback')" />
    </aside>
    <main :class="cn('main')">
      <div :class="cn('nav')">
        <BackButton @back="goBack" />
        <LogoLink />
      </div>
      <div :class="cn('body')">
        <div :class="cn('center')">
          <div :class="cn('slot')">
            <slot />
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<style lang="scss" scoped>
.account-auth-layout {
  --auth-layout-content-offset-top: 170px;

  display: grid;
  min-height: 100vh;
  background-color: #f6f7f8;
  grid-template-columns: minmax(0, 720px) minmax(0, 720px);
  justify-content: center;

  &__visual {
    position: relative;
    width: 100%;
    margin-left: auto;
    background-color: #73805b;
  }

  &__image {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__fallback {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      145deg,
      color-mix(in srgb, var(--color-primary, #73805b) 88%, #000) 0%,
      color-mix(in srgb, var(--color-primary, #73805b) 55%, #1a1a1a) 100%
    );
  }

  &__main {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-height: 0;
    position: relative;
    padding: 16px;
    padding-top: var(--auth-layout-content-offset-top);
    box-sizing: border-box;
    overflow: hidden;
  }

  &__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    width: 100%;
  }

  &__center {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100%;
    box-sizing: border-box;
  }

  &__slot {
    display: flex;
    justify-content: center;
    width: 100%;
    margin-block: auto;
    flex-shrink: 0;
    padding-bottom: 40px;
    box-sizing: border-box;
  }

  &__nav {
    position: absolute;
    top: 66px;
    display: flex;
    align-items: center;
    gap: 40px;
    margin-right: 64px;
    z-index: 1;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;

    &__visual {
      display: none;
    }
  }

  @media (max-width: 768px) {
    --auth-layout-content-offset-top: 120px;

    &__nav {
      top: 16px;
      gap: 58px;
      margin-right: 84px;
    }
  }
}
</style>
